import { z } from "zod";

// Bump for incompatible draft changes. Older namespaces are ignored, not migrated.
export const CHECKOUT_STORAGE_VERSION = 1;
const checkoutStoragePrefix = `checkout:v${CHECKOUT_STORAGE_VERSION}`;

export const checkoutSteps = ["tickets", "details", "phone", "profile", "questions", "email", "payment"] as const;
export type CheckoutStage = (typeof checkoutSteps)[number] | "status";

export const checkoutDraftSchema = z.object({
  step: z.enum(checkoutSteps).default("tickets"),
  ticketVariationID: z.string().max(100).default(""),
  discountCode: z.string().max(100).default(""),
  phone: z.string().max(40).default(""),
  phoneInput: z.string().max(60).default(""),
  phoneCountry: z.string().max(2).default(""),
  verificationSentAt: z.number().int().nonnegative().default(0),
  contactEmail: z.string().max(254).default(""),
  profile: z.object({
    name: z.string().max(100).default(""),
    age: z.string().max(3).default(""),
    gender: z.string().max(100).default(""),
    headline: z.string().max(500).default(""),
    profileImageUrl: z.string().max(2048).default(""),
    stage: z.string().max(30).default(""),
  }).default({ name: "", age: "", gender: "", headline: "", profileImageUrl: "", stage: "" }),
  answers: z.record(z.string(), z.object({
    text: z.string().max(5000).default(""),
    selected: z.array(z.string().max(1000)).max(100).default([]),
  })).refine(answers => Object.keys(answers).length <= 100).default({}),
  message: z.string().max(500).default(""),
  walletIntentID: z.string().max(100).default(""),
  paymentPhase: z.enum(["overview", "method", "processing"]).default("overview"),
});
export type CheckoutDraft = z.infer<typeof checkoutDraftSchema>;

export const emptyCheckoutDraft = (): CheckoutDraft => checkoutDraftSchema.parse({});

export function checkoutRecoveryDraft(walletIntentID: string): CheckoutDraft {
  return { ...emptyCheckoutDraft(), walletIntentID, paymentPhase: "processing" };
}

export function checkoutStorageKey(eventId: string, userId?: string): string {
  return `${checkoutStoragePrefix}:${userId ? `user:${userId}` : "visitor"}:${eventId}`;
}

export function activateCheckoutIdentity(storage: Storage, userId?: string): void {
  const identity = userId ? `user:${userId}` : "visitor";
  const identityKey = `${checkoutStoragePrefix}:identity`;
    const previous = storage.getItem(identityKey);
  if (previous && previous !== identity) {
    const prefix = `${checkoutStoragePrefix}:${previous}:`;
    for (let i = storage.length - 1; i >= 0; i--) {
      const key = storage.key(i);
      if (key?.startsWith(prefix)) storage.removeItem(key);
    }
  }
  storage.setItem(identityKey, identity);
}

export function readCheckoutDraft(storage: Storage, key: string, now = Date.now()): CheckoutDraft | null {
  const value = storage.getItem(key);
  if (!value) return null;
  try {
    const stored = JSON.parse(value);
    // Older tabs saved status screens as checkout progress. Keep only their
    // recovery handle until the API verifies the outcome, never their old form.
    if (stored.draft?.step === "status") {
      const { walletIntentID } = checkoutDraftSchema.parse({ walletIntentID: stored.draft.walletIntentID });
      stored.draft = checkoutRecoveryDraft(walletIntentID);
    }
    const saved = z.object({ expiresAt: z.number(), draft: checkoutDraftSchema }).parse(stored);
    if (saved.expiresAt > now) return saved.draft;
  } catch { /* Discard expired or incompatible browser drafts, not the checkout itself. */ }
  storage.removeItem(key);
  return null;
}

export function writeCheckoutDraft(storage: Storage, key: string, draft: CheckoutDraft, now = Date.now()): void {
  // Persist only unfinished input, never credentials, card data or default fields.
  const parsed = checkoutDraftSchema.parse(draft);
  const empty = emptyCheckoutDraft();
  const fields = Object.fromEntries(Object.entries(parsed).filter(([field, value]) =>
    JSON.stringify(value) !== JSON.stringify(empty[field as keyof CheckoutDraft])));
  if (!Object.keys(fields).length) { storage.removeItem(key); return; }
  storage.setItem(key, JSON.stringify({ expiresAt: now + 24 * 60 * 60 * 1000, draft: fields }));
}

export function restoreCheckoutURL(search: string, draft: CheckoutDraft): URLSearchParams {
  const next = new URLSearchParams(search);
  next.set("step", draft.step);
  if (draft.ticketVariationID) next.set("ticketVariationID", draft.ticketVariationID);
  else next.delete("ticketVariationID");
  if (draft.discountCode) next.set("discountCode", draft.discountCode);
  else next.delete("discountCode");
  return next;
}

type OutcomeIntent = { state: string; walletIntentEventTicketPurchase?: { eventID: string; eventRequestID?: string | null } | null };
type OutcomeEvent = { isAttending: boolean; myRequest?: { id: string; state: string; submittedAt?: string | null } | null };

export function checkoutOutcome(eventId: string, intent: OutcomeIntent | null | undefined, event: OutcomeEvent | null | undefined) {
  const purchase = intent?.walletIntentEventTicketPurchase;
  if (!purchase || purchase.eventID !== eventId) return "missing";
  if (intent?.state === "PROCESSED" && event?.isAttending) return "confirmed";
  const request = event?.myRequest;
  if (purchase.eventRequestID && request?.id === purchase.eventRequestID && request.submittedAt && request.state !== "APPROVING") return "request";
  if (intent?.state === "CANCELLED" || intent?.state === "FAILED") return "failed";
  return "processing";
}

export function anonymousCheckoutTransfer(draft: CheckoutDraft): CheckoutDraft {
  // Only the cart and phone challenge cross the sign-in boundary, never another profile or payment.
  return { ...emptyCheckoutDraft(), ticketVariationID: draft.ticketVariationID, discountCode: draft.discountCode,
    phone: draft.phone, phoneInput: draft.phoneInput, phoneCountry: draft.phoneCountry, verificationSentAt: draft.verificationSentAt, step: draft.step };
}
